import React from 'react';
import { Notification } from '@/lib/types';
import { formatDistanceToNow } from 'date-fns';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { X, BookOpen, Mail, FileText, Inbox, Calendar, Bell } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface NotificationListProps {
  notifications: Notification[];
  isLoading: boolean;
  onDelete: (id: number) => void;
}

export default function NotificationList({
  notifications,
  isLoading,
  onDelete
}: NotificationListProps) {
  
  // Get notification icon based on type
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'booking':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case 'contact':
        return <Mail className="h-4 w-4 text-green-500" />;
      case 'document':
        return <FileText className="h-4 w-4 text-yellow-500" />;
      case 'invoice':
        return <FileText className="h-4 w-4 text-red-500" />;
      case 'message':
        return <Inbox className="h-4 w-4 text-purple-500" />;
      case 'task':
        return <BookOpen className="h-4 w-4 text-orange-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };
  
  if (isLoading) {
    return (
      <div className="p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-start space-x-4 mb-4">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (notifications.length === 0) {
    return (
      <div className="py-6 px-4 text-center text-muted-foreground">
        <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No notifications yet</p>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-[300px]">
      <div className="p-2">
        {notifications.map((notification) => (
          <div 
            key={notification.id} 
            className={`
              flex items-start p-3 rounded-md mb-1
              ${notification.read ? 'bg-background' : 'bg-muted/30'}
              hover:bg-muted transition-colors
            `}
          >
            <div className="mt-0.5 mr-3">
              {getNotificationIcon(notification.type)}
            </div>
            
            <div className="flex-1 space-y-1">
              <div className="flex justify-between">
                <p className="text-sm font-medium">{notification.title}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 rounded-full"
                  onClick={() => onDelete(notification.id)}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Delete</span>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {notification.message}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
              </p>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}